import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { type ChangeEvent, useMemo, useState } from "react";
import type { TestMatrixOption, TestVariable } from "../../../types";
import { type ParsedData, parseCSV } from "../../../utils/import-parser";

interface Props {
  open: boolean;
  onClose: () => void;
  existingCount: number;
  onImport: (options: TestMatrixOption[]) => void;
  isSubmitting: boolean;
}

interface ValidatedCsv {
  parsed: ParsedData;
  rawHeaders: string[];
}

const readRawHeader = (file: File): Promise<string[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? "");
      const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
      // Naive CSV split is fine for validating emptiness/duplicates of a header row.
      resolve(firstLine.split(",").map((c) => c.trim()));
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });

const rowToMatrixOption = (row: Record<string, unknown>, columns: string[]): TestMatrixOption => ({
  id: crypto.randomUUID(),
  is_default: false,
  var_options: columns.map<TestVariable>((key) => ({
    key,
    value: String(row[key] ?? ""),
    type: "custom",
    lifetime: "test",
    context: "test",
    is_sensitive: false,
  })),
});

export const MatrixCsvImportDialog = ({
  open,
  onClose,
  existingCount,
  onImport,
  isSubmitting,
}: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [validated, setValidated] = useState<ValidatedCsv | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const reset = () => {
    setFile(null);
    setValidated(null);
    setError(null);
    setIsParsing(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    // Allow re-picking the same file later.
    e.target.value = "";
    if (!picked) return;

    setFile(picked);
    setValidated(null);
    setError(null);
    setIsParsing(true);

    try {
      const rawHeaders = await readRawHeader(picked);
      const blanks = rawHeaders
        .map((h, i) => ({ h, i }))
        .filter(({ h }) => h.length === 0)
        .map(({ i }) => i + 1);
      if (blanks.length > 0) {
        throw new Error(
          `Header row has empty name${blanks.length > 1 ? "s" : ""} at column ${blanks.join(", ")}. Every column needs a name.`,
        );
      }
      const seen = new Set<string>();
      const dupes = new Set<string>();
      for (const h of rawHeaders) {
        if (seen.has(h)) dupes.add(h);
        seen.add(h);
      }
      if (dupes.size > 0) {
        throw new Error(
          `Duplicate header name${dupes.size > 1 ? "s" : ""}: ${[...dupes].join(", ")}. Each column name must be unique.`,
        );
      }

      const parsed = await parseCSV(picked, 0);
      if (parsed.columns.length === 0) {
        throw new Error("CSV has no columns.");
      }
      if (parsed.rows.length === 0) {
        throw new Error("CSV has no data rows — only a header was found.");
      }
      setValidated({ parsed, rawHeaders });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse CSV");
    } finally {
      setIsParsing(false);
    }
  };

  const canImport = useMemo(
    () => !!validated && !isSubmitting && !isParsing && !error,
    [validated, isSubmitting, isParsing, error],
  );

  const handleImport = () => {
    if (!validated) return;
    const options = validated.parsed.rows.map((row) =>
      rowToMatrixOption(row, validated.parsed.columns),
    );
    onImport(options);
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Import matrix options from CSV</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Upload a CSV where the header row contains variable names and each subsequent row
            becomes one matrix option.{" "}
            <Link
              href="/matrix-options-example.csv"
              download="matrix-options-example.csv"
              underline="hover"
            >
              Download example
            </Link>
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={isSubmitting || isParsing}
            >
              {file ? "Choose a different file" : "Choose CSV file"}
              <input type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
            </Button>
            {file && (
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                {file.name}
              </Typography>
            )}
          </Stack>

          {isParsing && (
            <Typography variant="body2" color="text.secondary">
              Parsing…
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {validated && !error && (
            <Alert severity="info">
              {validated.parsed.rows.length} option
              {validated.parsed.rows.length === 1 ? "" : "s"} · {validated.parsed.columns.length}{" "}
              column
              {validated.parsed.columns.length === 1 ? "" : "s"}:{" "}
              {validated.parsed.columns.join(", ")}
            </Alert>
          )}

          {validated && !error && existingCount > 0 && (
            <Alert severity="warning">
              This will overwrite {existingCount} existing matrix option
              {existingCount === 1 ? "" : "s"}.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleImport} variant="contained" disabled={!canImport}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};
